import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { billing, BillingSummary } from '../lib/api';
import { useAuth } from '../lib/auth';
import './billing.css';

// PayPro's customer hub — order lookup, invoice downloads and payment-method
// changes all live behind it. Deliberately NOT the portal login: buyers are
// never given a password, so the login page alone is a dead end for most.
const PAYPRO_HELP = 'https://payproglobal.com/customer-support/';

const REASONS = [
  'Too expensive',
  'Not using it enough',
  'Missing a feature I need',
  "It didn't work well",
  'Other',
];

// "Feb 14, 2026" — year dropped only when it's the current one, so a renewal a
// few weeks out reads "March 12" while an annual one keeps its year.
function fmtDate(iso: string | null | undefined, dropThisYear = false): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: dropThisYear && sameYear ? 'long' : 'short',
    day: 'numeric',
    ...(dropThisYear && sameYear ? {} : { year: 'numeric' }),
  });
}

// PayPro sends amounts as strings ("6.49"). Render as currency, falling back to
// the raw value if it isn't numeric so we never print "$NaN".
function fmtAmount(amount: string | null | undefined, currency = 'USD'): string | null {
  if (!amount) return null;
  const n = Number(amount);
  if (!isFinite(n)) return String(amount);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function planLabel(kind: BillingSummary['plan_kind']): { name: string; price: string; sub?: string } {
  switch (kind) {
    case 'lifetime':
      return { name: "Founder's Lifetime", price: '$99 one-time — yours forever.' };
    case 'annual':
      return { name: 'Pro', price: '$39 / year', sub: '≈ $3.25 / mo' };
    case 'monthly':
      return { name: 'Pro', price: '$5.95 / month' };
    default:
      return {
        name: 'Free',
        price: 'Pro unlocks unlimited separations, 5-minute loops and exporting your own uploads.',
      };
  }
}

export function Billing() {
  const { user, refresh } = useAuth();
  const [sum, setSum] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);

  const load = useCallback(() => {
    return billing
      .summary()
      .then(setSum)
      .catch(() => setErr('We couldn\'t load your billing details. Refresh to try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function startCheckout(plan: 'monthly' | 'annual' | 'lifetime' = 'monthly') {
    setErr(null);
    try {
      const { url } = await billing.checkoutSession(plan);
      window.location.href = url;
    } catch (e: any) {
      setErr(e?.detail || 'Could not start checkout.');
    }
  }

  function copyOrder() {
    if (!sum?.order_id) return;
    void navigator.clipboard?.writeText(String(sum.order_id)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  // After a successful cancel, re-read the summary AND the session user: the
  // plan gate is unchanged (they keep Pro through the paid period) but the page
  // must flip to "Ending" immediately.
  async function afterCancel() {
    setDlgOpen(false);
    await load();
    void refresh?.();
  }

  if (loading) {
    return (
      <div className="bil-page">
        <h1 className="bil-h1">Billing</h1>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <div className="rl-spinner" style={{ margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  const s: BillingSummary = sum ?? {
    state: user?.plan === 'pro' ? 'active' : 'free',
    plan: user?.plan === 'pro' ? 'pro' : 'free',
    plan_kind: (user?.plan_kind as BillingSummary['plan_kind']) ?? 'free',
    billing_email: user?.email ?? '',
    pro_since: null,
    renews_on: null,
    renew_amount: null,
    last_payment: null,
    order_id: null,
    pro_until: null,
    canceled_at: null,
    can_cancel: false,
  };

  const isPaidCard = s.state === 'active' || s.state === 'ending' || s.state === 'lifetime';
  const { name, price, sub } = planLabel(isPaidCard ? s.plan_kind : 'free');
  const lifetime = s.state === 'lifetime';
  const renewsOn = fmtDate(s.renews_on, true);
  // Once cancelled this is the stamped pro_until. While still active it's the
  // next renewal — the date the cancel dialog must promise, and the same one the
  // server derives. Null on either → the dialog uses its date-free wording.
  const endsOn = fmtDate(s.pro_until ?? s.renews_on, true);
  const lastPayDate = fmtDate(s.last_payment?.date);
  const lastPayAmt = fmtAmount(s.last_payment?.amount, s.last_payment?.currency);

  const badge =
    s.state === 'active' ? (
      <span className="bil-badge bil-badge-active">
        <span className="bil-dot" />
        Active
      </span>
    ) : s.state === 'ending' ? (
      <span className="bil-badge bil-badge-ending">Ending</span>
    ) : s.state === 'lifetime' ? (
      <span className="bil-badge bil-badge-gold">Founder</span>
    ) : s.state === 'ended' ? (
      <span className="bil-badge bil-badge-ended">Pro ended</span>
    ) : null;

  return (
    <div className="bil-page">
      <h1 className="bil-h1">Billing</h1>

      {err && (
        <div className="bil-notice bil-notice-err">
          <div className="bil-notice-body">{err}</div>
        </div>
      )}

      {s.state === 'ended' && (
        <div className="bil-notice">
          <div className="bil-notice-body">
            <div className="bil-notice-title">
              Your Pro plan ended{fmtDate(s.canceled_at) ? ` on ${fmtDate(s.canceled_at)}` : ''}.
            </div>
            <div className="bil-notice-sub">
              Your loops and library are still here — Pro features are paused.
            </div>
          </div>
        </div>
      )}

      <div
        className="bil-card bil-plan"
        style={
          lifetime
            ? {
                borderColor: 'var(--gold-border)',
                background:
                  'linear-gradient(180deg,var(--gold-soft),transparent 180px),var(--panel)',
              }
            : isPaidCard
              ? undefined
              : { paddingBottom: 28 }
        }
      >
        <div className="bil-plan-top">
          <span className="bil-eyebrow">Current plan</span>
          {badge}
        </div>

        <div className="bil-hero">
          <div className="bil-fact">
            <div className={lifetime ? 'bil-planname bil-planname-gold' : 'bil-planname'}>{name}</div>
            <div className="bil-price bil-num">
              {price}
              {sub && <div className="bil-charge-sub bil-num">{sub}</div>}
            </div>
          </div>

          {/* Renewal is omitted entirely when unknown — no placeholder row. */}
          {s.state === 'active' && renewsOn && (
            <div className="bil-fact">
              <div className="bil-fact-label">Renews on</div>
              <div className="bil-charge bil-num">{renewsOn}</div>
              {fmtAmount(s.renew_amount) && (
                <div className="bil-charge-sub bil-num">{fmtAmount(s.renew_amount)}</div>
              )}
            </div>
          )}

          {s.state === 'ending' && (
            <div className="bil-fact">
              <div className="bil-ending-line">
                {endsOn ? (
                  <>
                    Your plan ends on <b>{endsOn}</b> — you keep everything until then.
                  </>
                ) : (
                  <>Your plan ends at the end of your current billing period — you keep everything until then.</>
                )}
              </div>
            </div>
          )}

          {(s.state === 'ending' || s.state === 'ended' || s.state === 'free') && (
            <>
              <div className="bil-spacer" />
              <div className="bil-row">
                {!isPaidCard && (
                  <Link className="bil-btn bil-btn-ghost" to="/pricing">
                    Compare plans →
                  </Link>
                )}
                <button className="bil-btn bil-btn-primary" onClick={() => void startCheckout()}>
                  {s.state === 'free' ? 'Upgrade to Pro' : 'Resubscribe to Pro'}
                </button>
              </div>
            </>
          )}
        </div>

        {isPaidCard && (
          <>
            <div className="bil-rule" />
            <div className="bil-meta">
              <div>
                <div className="bil-meta-k">Billing email</div>
                <div className="bil-meta-v">{s.billing_email}</div>
              </div>
              {fmtDate(s.pro_since) && (
                <div>
                  <div className="bil-meta-k">Pro since</div>
                  <div className="bil-meta-v bil-num">{fmtDate(s.pro_since)}</div>
                </div>
              )}
              {lastPayAmt && (
                <div>
                  <div className="bil-meta-k">Last payment</div>
                  <div className="bil-meta-v bil-num">
                    {[lastPayDate, lastPayAmt].filter(Boolean).join(' · ')}
                  </div>
                </div>
              )}
            </div>

            <div className="bil-planfoot">
              <div className="bil-planfoot-l">
                <a
                  className="bil-link bil-link-underline"
                  href={PAYPRO_HELP}
                  target="_blank"
                  rel="noreferrer"
                >
                  {lifetime ? 'Download invoices ↗' : 'Update payment method or download invoices ↗'}
                </a>
                {s.order_id && (
                  <span className="bil-copyrow">
                    Order <span className="bil-mono">#{s.order_id}</span>
                    <button className="bil-copy" onClick={copyOrder}>
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </span>
                )}
              </div>
              {s.can_cancel && (
                <button
                  className="bil-link bil-link-underline"
                  onClick={() => setDlgOpen(true)}
                >
                  Cancel subscription
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <div className="bil-foot">
        <div className="bil-foot-lock">
          <LockIcon />
          <span>
            Payments are processed securely by PayPro Global, our merchant of record. Your card
            details never touch Riffloop&apos;s servers.
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-faint)' }}>
          Billing questions →{' '}
          <a href="mailto:riffloopapp@gmail.com" style={{ color: 'var(--text-faint)' }}>
            riffloopapp@gmail.com
          </a>
        </div>
      </div>

      <Footer />

      {dlgOpen && (
        <CancelDialog
          endsOn={endsOn}
          onClose={() => setDlgOpen(false)}
          onDone={afterCancel}
        />
      )}
    </div>
  );
}

function LockIcon() {
  return (
    <svg className="bil-notice-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function CancelDialog({
  endsOn,
  onClose,
  onDone,
}: {
  endsOn: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const [phase, setPhase] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [until, setUntil] = useState<string | null>(endsOn);

  // Escape closes — but never mid-request, so a cancel can't be half-observed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'submitting') (phase === 'done' ? onDone : onClose)();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, onClose, onDone]);

  const keepUntil = until ? (
    <>
      until <b>{until}</b>
    </>
  ) : (
    <>until the end of your current billing period</>
  );

  async function submit() {
    setPhase('submitting');
    setError(null);
    try {
      const res = await billing.cancel(reason === 'Other' ? other.trim() || 'Other' : reason);
      if (res.pro_until) {
        const d = fmtDate(res.pro_until, true);
        if (d) setUntil(d);
      }
      setPhase('done');
    } catch (e: any) {
      setError(e?.detail || "We couldn't cancel your subscription. Try again, or email riffloopapp@gmail.com.");
      setPhase('idle');
    }
  }

  if (phase === 'done') {
    return (
      <div className="bil-overlay" onMouseDown={(e) => e.target === e.currentTarget && onDone()}>
        <div className="bil-dlg" role="dialog" aria-modal="true">
          <div className="bil-check">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <h2 className="bil-dlg-title">Your subscription is cancelled</h2>
          <p className="bil-dlg-sub">
            You&apos;ll keep Pro {keepUntil}. After that your account moves to Free — nothing
            you&apos;ve made goes away.
          </p>
          <div className="bil-dlg-actions">
            <button className="bil-btn bil-btn-primary bil-btn-block" onClick={onDone}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  const busy = phase === 'submitting';

  return (
    <div
      className="bil-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div className="bil-dlg" role="dialog" aria-modal="true" aria-label="Cancel your Pro subscription">
        <div className={busy ? 'bil-dlg-lock' : undefined}>
          <h2 className="bil-dlg-title">Cancel your Pro subscription?</h2>
          <p className="bil-dlg-sub">You&apos;ll keep Pro {keepUntil}.</p>

          <div className="bil-dlg-k">After that you&apos;ll lose</div>
          <ul className="bil-loses">
            <li>Unlimited song separations (Free is 5 per day)</li>
            <li>Loops up to 7 minutes (Free is 1 minute)</li>
            <li>Best separation quality</li>
            <li>Exporting your own uploads</li>
          </ul>

          <div className="bil-dlg-rule" />

          <div className="bil-dlg-k">
            Why are you cancelling? <span className="bil-optional">(optional)</span>
          </div>
          <div className="bil-reasons">
            {REASONS.map((r) => (
              <label className="bil-reason" key={r}>
                <input
                  type="radio"
                  name="bil-cancel-reason"
                  checked={reason === r}
                  onChange={() => setReason(r)}
                />
                {r}
              </label>
            ))}
          </div>
          {reason === 'Other' && (
            <input
              className="bil-input"
              placeholder="What made you cancel?"
              maxLength={200}
              value={other}
              onChange={(e) => setOther(e.target.value)}
            />
          )}
        </div>

        {error && (
          <div className="bil-notice bil-notice-err" style={{ marginTop: 18 }}>
            <div className="bil-notice-body">
              <div className="bil-notice-title">We couldn&apos;t cancel your subscription.</div>
              <div className="bil-notice-sub">
                Try again, or email{' '}
                <a href="mailto:riffloopapp@gmail.com" style={{ color: 'var(--text-dim)' }}>
                  riffloopapp@gmail.com
                </a>
                .
              </div>
            </div>
          </div>
        )}

        <div className="bil-dlg-actions">
          <button className="bil-btn bil-btn-primary bil-btn-block" onClick={onClose} disabled={busy}>
            Keep my Pro plan
          </button>
          <button className="bil-btn bil-btn-quiet bil-btn-block" onClick={() => void submit()} disabled={busy}>
            {busy ? (
              <>
                <span className="bil-spinner" />
                Cancelling…
              </>
            ) : error ? (
              'Try again'
            ) : (
              'Cancel subscription'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
